import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccountingPeriodReturn,
  ReconciliationList as ReconciliationListData,
  SubledgerTieOut,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  sameDecimal,
  savePdfResponse,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
  canReconcile: boolean;
  /** Closing a period and generating the reports. Owner/Admin. */
  canManageChart: boolean;
}

const periodLabel = (p: AccountingPeriodReturn): string =>
  `FY${p.fiscal_year} P${p.period_number} · ${formatBillingDate(
    p.start_date
  )} – ${formatBillingDate(p.end_date)}`;

/** One numbered step of the checklist. */
const Step: React.FC<{
  number: number;
  title: string;
  status: React.ReactNode;
  children?: React.ReactNode;
}> = ({ number, title, status, children }) => (
  <div className="py-4">
    <div className="flex items-baseline justify-between gap-4">
      <h4 className="text-sm font-medium text-zinc-950 dark:text-white">
        {number}. {title}
      </h4>
      <div className="shrink-0 text-sm text-zinc-950 dark:text-white">
        {status}
      </div>
    </div>
    {children && <div className="mt-2">{children}</div>}
  </div>
);

/**
 * The period checklist — the screen an accountant opens on the first
 * working day of the month.
 *
 * Steps 1 to 3 are read-only aggregations of endpoints that already
 * exist. Step 4 closes the period and step 5 downloads the reports.
 *
 * **Reconciled is not closed, and this screen says so.** Completing a
 * reconciliation sets one account's status; it does not close the
 * period and does not stop anything posting into it. Only step 4 does
 * that, and every system that blurs the two confuses people.
 */
const MonthEndClose: React.FC<Props> = ({
  hoaId,
  canReconcile,
  canManageChart,
}) => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState<AccountingPeriodReturn[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [recs, setRecs] = useState<ReconciliationListData | null>(null);
  const [tieOut, setTieOut] = useState<SubledgerTieOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await ledgerService.listPeriods({ hoaId });
        const real = data.periods.filter((p) => !p.is_opening);
        setPeriods(real);
        // Open on the period we are IN, not the newest one that
        // happens to be open.
        //
        // A whole fiscal year is generated at once and every period
        // starts open, so "the latest open period" is December — a
        // month with no activity in it, which makes the checklist look
        // broken on the screen an accountant opens first. What they
        // want is the month they are closing: the one containing today,
        // or failing that the most recent one that has actually ended.
        //
        // Dates are ISO strings, so lexicographic comparison is date
        // comparison.
        const today = new Date().toISOString().slice(0, 10);
        const current = real.find(
          (p) => p.start_date <= today && p.end_date >= today
        );
        const ended = [...real].reverse().find((p) => p.end_date <= today);
        setPeriodId(
          current?.id ?? ended?.id ?? real[real.length - 1]?.id ?? ''
        );
      } catch (err) {
        setError(billingErrorMessage(err, "Couldn't load the periods."));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [hoaId]);

  const period = periods.find((p) => p.id === periodId) ?? null;

  const loadPeriod = useCallback(async () => {
    if (!period) return;
    try {
      const [list, tie] = await Promise.all([
        ledgerService.listReconciliations({
          hoaId,
          from: period.start_date,
          to: period.end_date,
        }),
        ledgerService.getTieOut({ hoaId }),
      ]);
      setRecs(list.data);
      setTieOut(tie.data);
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't load this period."));
    }
  }, [hoaId, period]);

  useEffect(() => {
    void loadPeriod();
  }, [loadPeriod]);

  const closePeriod = async () => {
    if (!period) return;
    try {
      setBusy(true);
      setError(null);
      await ledgerService.setPeriodStatus({
        hoaId,
        periodId: period.id,
        periodStatusUpdate: { status: 'closed' },
      });
      setConfirmClose(false);
      const { data } = await ledgerService.listPeriods({ hoaId });
      setPeriods(data.periods.filter((p) => !p.is_opening));
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't close the period."));
    } finally {
      setBusy(false);
    }
  };

  const downloadReports = async () => {
    if (!period) return;
    try {
      setBusy(true);
      const response = await ledgerService.getFinancialReports(
        { hoaId, periodId: period.id },
        { responseType: 'blob' as const }
      );
      savePdfResponse(
        response,
        `financial-reports-${period.fiscal_year}-p${period.period_number}.pdf`
      );
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't generate the reports."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const done = recs
    ? recs.reconciliations.filter((r) => r.status === 'completed').length
    : 0;
  const reconcilable = recs
    ? recs.reconciliations.length + recs.unreconciled_accounts.length
    : 0;
  const allReconciled = reconcilable > 0 && done === reconcilable;
  const unexplained = recs
    ? recs.reconciliations.reduce((n, r) => n + r.unexplained_count, 0)
    : 0;
  const tieOutClean =
    !!tieOut &&
    sameDecimal(tieOut.receivable_drift, '0') &&
    sameDecimal(tieOut.prepaid_drift, '0');
  const closed = period?.status !== 'open';

  // The gate lives here, not in the backend. `set_period_status` will
  // close a period with an unreconciled account and should keep doing
  // so — a real association's first close will be messy in ways we
  // cannot predict, and a backend rule it cannot get past turns a bad
  // month into a support ticket. So: disabled with the reason stated,
  // and the confirm dialog lets an Owner/Admin proceed anyway.
  const closeBlockers: string[] = [];
  // Only a blocker when there is something to reconcile. An association
  // with no cash account linked to a bank account has nothing to
  // reconcile, and saying "0 of 0 cash accounts are not reconciled"
  // reads as a fault the accountant cannot clear. Step 1 already says
  // why the list is empty.
  if (reconcilable > 0 && done < reconcilable) {
    closeBlockers.push(
      `${reconcilable - done} of ${reconcilable} cash accounts are not reconciled`
    );
  }
  if (!tieOutClean) closeBlockers.push('the subledger tie-out is not clean');
  if (unexplained > 0) {
    closeBlockers.push(
      `${unexplained} bank item${unexplained === 1 ? '' : 's'} on the statements are unexplained`
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="That did not work"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="max-w-sm flex-1">
          <FieldPicker
            label="Accounting period"
            value={periodId}
            onChange={setPeriodId}
            options={periods.map((p) => ({
              value: p.id,
              label: periodLabel(p),
            }))}
          />
        </div>
        {period && (
          <Badge color={closed ? 'zinc' : 'green'}>
            Period: {period.status}
          </Badge>
        )}
      </div>

      <Divider className="my-4" />

      <Step
        number={1}
        title="Cash and investment accounts reconciled"
        status={
          <span
            className={
              allReconciled ? 'text-green-600 dark:text-green-400' : ''
            }
          >
            {done} of {reconcilable}
          </span>
        }
      >
        {recs && reconcilable === 0 && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No cash account is linked to a bank account yet.
          </p>
        )}
        <div className="space-y-1">
          {recs?.reconciliations.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-3 text-sm text-zinc-950 dark:text-white"
            >
              <span>
                {r.account_number} {r.account_name} ·{' '}
                {formatBillingDate(r.statement_date)}
              </span>
              <span className="flex items-center gap-2">
                {r.status === 'completed' ? (
                  <Badge color="green">completed</Badge>
                ) : sameDecimal(r.difference, '0') ? (
                  <Badge color="amber">balanced, not completed</Badge>
                ) : (
                  <Badge color="red">out by {formatMoney(r.difference)}</Badge>
                )}
                <Button
                  plain
                  onClick={() =>
                    navigate(`/hoa/${hoaId}/accounting/reconciliations/${r.id}`)
                  }
                >
                  {r.status === 'completed' ? 'View' : 'Reconcile'}
                </Button>
              </span>
            </div>
          ))}
          {recs?.unreconciled_accounts.map((a) => (
            <div
              key={a.gl_account_id}
              className="flex items-center justify-between gap-3 text-sm text-zinc-950 dark:text-white"
            >
              <span>
                {a.account_number} {a.account_name}
              </span>
              <span className="flex items-center gap-2">
                <Badge color="zinc">not started</Badge>
                {canReconcile && (
                  <Button
                    plain
                    onClick={() =>
                      navigate(`/hoa/${hoaId}/accounting/reconciliations`)
                    }
                  >
                    Start
                  </Button>
                )}
              </span>
            </div>
          ))}
        </div>
      </Step>

      <Divider />

      <Step
        number={2}
        title="Subledger tie-out"
        status={
          <span data-testid="tie-out-status">
            {tieOut ? (
              tieOutClean ? (
                <span className="text-green-600 dark:text-green-400">
                  clean
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400">drifted</span>
              )
            ) : (
              '—'
            )}
          </span>
        }
      >
        {tieOut && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {/*
              Two checks, never netted: a receivable drift and a prepaid
              drift of the same size are two faults, not zero.
            */}
            Receivable {formatMoney(tieOut.receivable_drift)} · Prepaid{' '}
            {formatMoney(tieOut.prepaid_drift)}
            {tieOut.homes_in_drift.length > 0 &&
              ` · ${tieOut.homes_in_drift.length} home${
                tieOut.homes_in_drift.length === 1 ? '' : 's'
              } affected`}
          </p>
        )}
      </Step>

      <Divider />

      <Step
        number={3}
        title="Unexplained bank items"
        status={
          unexplained === 0 ? (
            <span className="text-green-600 dark:text-green-400">none</span>
          ) : (
            <span>
              {unexplained} item{unexplained === 1 ? '' : 's'}
            </span>
          )
        }
      >
        {unexplained > 0 && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Money on a statement that our books do not explain. Open the
            reconciliation to post it or match it.
          </p>
        )}
      </Step>

      <Divider />

      <Step
        number={4}
        title="Close the period"
        status={
          closed ? (
            <Badge color="zinc">{period?.status}</Badge>
          ) : (
            canManageChart && (
              <Button
                color="sky"
                disabled={busy}
                onClick={() => setConfirmClose(true)}
              >
                Close period
              </Button>
            )
          )
        }
      >
        {!closed && closeBlockers.length > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Not ready: {closeBlockers.join('; ')}.
          </p>
        )}
        {!closed && closeBlockers.length === 0 && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Closing stops anything posting into this period. Reconciling an
            account does not.
          </p>
        )}
      </Step>

      <Divider />

      <Step
        number={5}
        title="Financial reports"
        status={
          <Button plain disabled={busy || !period} onClick={downloadReports}>
            Generate
          </Button>
        }
      >
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Five of the ten reports a board packet contains, in packet order. The
          four not yet generated keep their place, and the institution's own
          statements are attached separately.
        </p>
      </Step>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Close {period && periodLabel(period)}?</DialogTitle>
        <DialogBody>
          <p className="text-sm text-zinc-950 dark:text-white">
            Nothing can post into a closed period. A correction becomes a new
            entry in the next open one, never an edit to this.
          </p>
          {closeBlockers.length > 0 && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              This period is not ready: {closeBlockers.join('; ')}. You can
              close it anyway — a first close is often messy — but the reports
              for this period will carry that.
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setConfirmClose(false)}>
            Cancel
          </Button>
          <Button color="sky" disabled={busy} onClick={closePeriod}>
            Close period
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MonthEndClose;
